import { AxleButton } from '@/components/buttons/AxleButton';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { FeatureFlag } from '@/types/featureFlag';
import { VisitOutcome } from '@/types/visit';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { FC, useCallback } from 'react';

import {
    EditVisitServicesActionQuery,
    EditVisitServicesActionQueryVariables,
} from './EditVisitServicesButton.gql.types';

const FLAG = FeatureFlag.DASHBOARD_EDIT_VISIT_SERVICES_DIALOG;

// Fetches the visit fields + the feature flag in a single non-suspending query. Every visit field is
// keyed off the already-loaded `Visit`, so Apollo serves this cache-first without a network round trip
// most of the time. Non-suspending on purpose: this hook is consumed inside table action cells that are
// not wrapped in a Suspense boundary.
const EDIT_VISIT_SERVICES_ACTION_QUERY = gql`
    query EditVisitServicesAction($visitId: UUID!, $features: [String!]!) {
        visit(id: $visitId) {
            id
            outcome
            permissions {
                id
                editServices
            }
            organization {
                id
                config {
                    id
                    maxServicesPerAppointment
                }
            }
            clinicianAppointments {
                id
            }
        }
        featureFlags(keys: $features) {
            id
            enabled
        }
    }
`;

type EditVisitServicesAction = {
    canEditServices: boolean;
    label: string;
    open: () => void;
};

/**
 * Encapsulates the label + gating logic for the "Update Service(s)" action so surfaces only need to
 * pass a `visitId`. The action is available only for single-clinician-appointment visits with no
 * outcome, when the user has `editServices` permission and the feature flag is enabled.
 */
export const useEditVisitServicesAction = (visitId: string): EditVisitServicesAction => {
    const { openDialog } = useDialog();
    const { data } = useQuery<EditVisitServicesActionQuery, EditVisitServicesActionQueryVariables>(
        EDIT_VISIT_SERVICES_ACTION_QUERY,
        { variables: { visitId, features: [FLAG] } },
    );

    const visit = data?.visit ?? null;
    // `featureFlags` is typed non-null, but Apollo Client 4 can deliver a partial
    // `data` (e.g. when only some fields resolve from the cache) where it's absent,
    // so guard it. eslint's type-aware rule doesn't account for partial results.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const flagEnabled = data?.featureFlags?.find((flag) => flag.id === FLAG)?.enabled ?? false;
    const appointments = visit?.clinicianAppointments ?? [];
    const clinicianAppointmentId = appointments.length === 1 ? appointments[0].id : null;

    const canEditServices =
        flagEnabled &&
        visit !== null &&
        visit.permissions.editServices &&
        visit.outcome === VisitOutcome.NONE &&
        clinicianAppointmentId !== null;

    const label =
        visit?.organization.config.maxServicesPerAppointment === 1
            ? 'Update Service'
            : 'Update Services';

    const open = useCallback(() => {
        if (clinicianAppointmentId) {
            openDialog(DialogType.EditVisitServices, { clinicianAppointmentId });
        }
    }, [openDialog, clinicianAppointmentId]);

    return { canEditServices, label, open };
};

export const EditVisitServicesButton: FC<{ visitId: string }> = ({ visitId }) => {
    const { canEditServices, label, open } = useEditVisitServicesAction(visitId);

    if (!canEditServices) {
        return null;
    }

    return <AxleButton onClick={open}>{label}</AxleButton>;
};
